import React, { useRef, useState, useEffect, useCallback } from 'react';
import EditorAddIcon from '@atlaskit/icon/glyph/editor/add';
import { token } from '@atlaskit/tokens';
import { colors } from '@atlassian/jira-portfolio-3-common';
import { subscribeHorizontalScrolling } from '@atlassian/jira-portfolio-3-horizontal-scrolling';
import {
	type IssueTableItem,
	TABLE_ISSUE,
} from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/query/table';
import { getPositionsForBar } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/view/main/tabs/roadmap/timeline/schedule/row/bar/utils';
import {
	SCOPE_ROW_HEIGHT,
	HORIZONTAL_SCROLL_OFFSET,
} from '@atlassian/jira-portfolio-3-portfolio/src/common/view/constant';
import { useDepLinesDrag } from '../provider';
import type { DnDItemId, Rect } from '../provider/types';
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-global-styles -- Ignored via go/DSP-18766
import * as styles from './styles.module.css';
import type { Props } from './types';

/** Function computes the delta between drag scroll start and current timeline location.
 * @returns {Array} Returns the drag scrolling delta in px and functions to register and deregister a callback function and reset the calculations
 */
const useScrollingDelta = () => {
	const offsetInPxRef = useRef<number>(0);
	const startOffsetInPxRef = useRef<number>(0);
	// eslint-disable-next-line @typescript-eslint/no-empty-function
	const callbackRef = useRef(() => {});
	const deltaRef = useRef<number>(0);

	useEffect(() => {
		const unsubscribe = subscribeHorizontalScrolling(({ viewport, container }) => {
			const offsetInPx = (viewport.offset * container.width) / container.duration || 0;
			offsetInPxRef.current = offsetInPx;
			deltaRef.current = offsetInPxRef.current - startOffsetInPxRef.current;
			callbackRef.current();
		});

		return unsubscribe;
	}, [deltaRef]);

	/** Function used to start and end calculations for deltaRef */
	const dragReset = useCallback(() => {
		startOffsetInPxRef.current = offsetInPxRef.current;
		deltaRef.current = 0;
	}, []);

	/** Register callback to be used when delta changes */
	const register = useCallback((callback: () => void) => {
		callbackRef.current = callback;
	}, []);

	/** Deregister callback */
	const deregister = useCallback(() => {
		// eslint-disable-next-line @typescript-eslint/no-empty-function
		callbackRef.current = () => {};
	}, []);

	return [deltaRef, { dragReset, register, deregister }] as const;
};

/**
 * Gets the table item if it's issue item, return undefined otherwise.
 * @param scope the plan scope
 * @param rowId the identity of the row
 */
const getTableItem = (scope: Props['scope'], rowId?: DnDItemId): IssueTableItem | undefined => {
	if (rowId === undefined) {
		return;
	}
	const issueTableItem = scope[rowId.scopeIndex];

	if (issueTableItem === undefined || issueTableItem.tag !== TABLE_ISSUE) {
		return;
	}

	return issueTableItem;
};

/**
 * Returns the vertical offset of the (middle) issue bar.
 */
const getAnchorY = (scope: Props['scope'], rowId?: DnDItemId): number | undefined => {
	const issueTableItem = getTableItem(scope, rowId);
	if (issueTableItem?.topOffset) {
		// we remove 20 pixels from the top offset so that the anchor for the dependency line is located at the vertical center of the issue bar
		// if we wouldn't remove 20 pixels, the dependency line would be drawn from the bottom left/right of an issue bar to the bottom left/right of the issue bar it blocks
		return issueTableItem.topOffset - SCOPE_ROW_HEIGHT / 2;
	}
	return undefined;
};

/**
 * Returns the horizontal offset of the issue bar end (could be the left or the right end).
 */
const getAnchorX = (
	scope: Props['scope'],
	timelineRange: Props['timelineRange'],
	direction: string | null | undefined,
	viewportWidth: number,
	rowId?: DnDItemId,
): number | null | undefined => {
	const issueTableItem = getTableItem(scope, rowId);
	if (issueTableItem === undefined) {
		return undefined;
	}

	const fromPositions = getPositionsForBar(
		{
			baselineStart: issueTableItem.value.baselineStart,
			baselineEnd: issueTableItem.value.baselineEnd,
		},
		timelineRange,
	);

	return direction === 'inward'
		? (fromPositions.leftPositionPercentage * viewportWidth) / 100
		: ((100 - fromPositions.rightPositionPercentage) * viewportWidth) / 100;
};

const useMouseOffset = () => {
	const [value, setValue] = useState<{
		clientX: number | undefined;
		clientY: number | undefined;
	}>({ clientX: undefined, clientY: undefined });
	useEffect(() => {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		const handler = (e: any) => {
			e.preventDefault();
			setValue(e.detail);
		};

		window.addEventListener('customized.dragmove', handler, false);
		return () => window.removeEventListener('customized.dragmove', handler, false);
	}, []);
	return value;
};

export default function DepLineDragPreviewLayer({ timelineRange, width, scope, scrollTop }: Props) {
	const { dragSource, handleDragMove } = useDepLinesDrag();
	const { rowId, direction: sourceDirection } = dragSource || {
		rowId: undefined,
		direction: undefined,
	};
	const rootElRef = useRef<HTMLDivElement>(null);

	const [
		deltaRef,
		{ dragReset, register: registerDragScrolling, deregister: deregisterDragScrolling },
	] = useScrollingDelta();

	const [, setFlag] = useState<number>(0);

	const [clientRect, setClientRect] = useState<Rect>();

	const scrollTopRef = useRef(scrollTop);
	const originalScrollTopRef = useRef(scrollTop);
	const anchorX = getAnchorX(scope, timelineRange, sourceDirection, width, rowId);
	const anchorY = getAnchorY(scope, rowId);
	const { clientX: mouseX, clientY: mouseY } = useMouseOffset();

	// Cache the scrollTop into a ref
	useEffect(() => {
		scrollTopRef.current = scrollTop;
	}, [scrollTop]);

	useEffect(() => {
		const rootEl = rootElRef.current;
		if (rootEl == null) {
			return;
		}

		// eslint-disable-next-line @typescript-eslint/no-shadow
		const { top, left, width, height } = rootEl.getBoundingClientRect();
		setClientRect({ top, left, width, height });

		// Set the original scrollTop to add to the offset when there is scrolling while dragging
		originalScrollTopRef.current = scrollTopRef.current;
	}, [anchorX, anchorY]);

	const scrollTopDelta = scrollTop - originalScrollTopRef.current;

	let relativeMouseX =
		mouseX !== undefined && clientRect !== undefined ? mouseX - clientRect.left : undefined;

	if (relativeMouseX !== undefined) {
		relativeMouseX -= HORIZONTAL_SCROLL_OFFSET;
	}

	let targetX = relativeMouseX !== undefined ? relativeMouseX : undefined;

	if (targetX !== undefined) {
		targetX = targetX + HORIZONTAL_SCROLL_OFFSET + deltaRef.current;
	}

	const targetY =
		mouseY !== undefined && clientRect !== undefined
			? mouseY - (clientRect.top - scrollTopDelta)
			: undefined;

	useEffect(() => {
		if (targetX === undefined || targetY === undefined || rowId == null) {
			return;
		}

		handleDragMove(targetX, targetY);
	}, [targetX, targetY, handleDragMove, rowId]);

	const shouldDraw =
		anchorX !== undefined &&
		anchorY !== undefined &&
		targetX !== undefined &&
		targetY !== undefined &&
		rowId !== undefined;

	useEffect(() => {
		if (shouldDraw) {
			const handler = () => {
				setFlag((flag) => flag + 1);
			};
			dragReset();
			registerDragScrolling(handler);
			return () => deregisterDragScrolling();
		}
		dragReset();
	}, [deregisterDragScrolling, dragReset, registerDragScrolling, shouldDraw]);

	return (
		<div
			style={{
				// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
				position: 'absolute',
				// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
				inset: 0,
				// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
				zIndex: 3,
				pointerEvents: shouldDraw ? 'initial' : 'none',

				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
				marginLeft: -HORIZONTAL_SCROLL_OFFSET,
			}}
			ref={rootElRef}
		>
			<div
				style={{
					// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
					position: 'absolute',

					top: (targetY ?? 0) - 8,

					left: (targetX ?? 0) - 8,
					opacity: shouldDraw ? 1 : 0,
				}}
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
				className={`${styles.marker} ${styles['marker-drag-preview']}`}
			>
				<EditorAddIcon label="" size="small" />
			</div>

			<svg width={2e6} height={clientRect?.height ?? 0} opacity={shouldDraw ? 1 : 0}>
				<line
					// @ts-expect-error - TS2322 - Type 'number | null | undefined' is not assignable to type 'string | number | undefined'.
					x1={anchorX != null ? anchorX + HORIZONTAL_SCROLL_OFFSET : anchorX}
					y1={anchorY}
					x2={targetX}
					y2={targetY}
					stroke={token('color.border.bold', colors.N100)}
					strokeWidth="2px"
					strokeLinecap="round"
				/>
			</svg>
		</div>
	);
}
